// The list of all attachments (R-M2), loaded once and shared by the screen «Файлы» and its left column; the chosen section is in the
// address (?section=), so the column and the screen agree and a link keeps the choice.

import { useEffect, useSyncExternalStore } from 'react';
import { useSync } from '../sync/react.tsx';

/** `form` — a file of a form's answer; `record` — a file put into a record's link field (J7). */
export interface FileRow { source: 'form' | 'record'; key: string; name: string; size: number; type: string; at: string; form: string; field: string; author: string; recordId: string | null; recordTitle: string; typeId: string | null; typeName: string }
export const NONE = '—';

type State = { rows: FileRow[] | null; error: string | null; truncated: boolean; user: string | null };
let state: State = { rows: null, error: null, truncated: false, user: null };
let loading = false;
const listeners = new Set<() => void>();
const set = (s: State) => { state = s; for (const l of listeners) l(); };

/**
 * The rows, fetched on the first use of a session; the screen reloads them on its own visit to stay fresh. The rows belong to the
 * person they were fetched for: another person signed in on the same tab starts from nothing (review P3).
 */
export function useFiles(reload = false): State {
  const user = useSync().header?.userId ?? null;
  const s = useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => state);
  useEffect(() => {
    if (state.user !== user) { loading = false; set({ rows: null, error: null, truncated: false, user }); }
    if (loading || (state.rows && !reload)) return;
    loading = true;
    fetch('/api/files-index').then(async (r) => { if (!r.ok) throw new Error(); const j = (await r.json()) as { items: FileRow[]; truncated?: boolean }; if (state.user === user) set({ rows: j.items, error: null, truncated: !!j.truncated, user }); })
      .catch(() => { if (state.user === user) set({ ...state, error: 'Не удалось загрузить список файлов' }); })
      .finally(() => { loading = false; });
  }, [reload, user]);
  return s.user === user ? s : { rows: null, error: null, truncated: false, user };
}

/**
 * «Ссылка» of a file (R-C16): a form file by the setting — by rights or temporary; a record's file — its address, which opens for
 * those who read the record. Copied to the clipboard, the toast says which one it is.
 */
export async function copyFileLink(row: FileRow, toast: (t: { kind: 'info' | 'error'; text: string }) => void): Promise<void> {
  let url = `/api/files/${row.key}`, expiresAt: string | null = null;
  if (row.source === 'form') {
    const r = await fetch('/api/files/link', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ key: row.key }) });
    if (!r.ok) { toast({ kind: 'error', text: 'Ссылку выдать нельзя' }); return; }
    ({ url, expiresAt } = (await r.json()) as { url: string; expiresAt: string | null });
  }
  await navigator.clipboard?.writeText(new URL(url, location.origin).href).catch(() => {});
  toast({ kind: 'info', text: expiresAt ? `Временная ссылка скопирована — до ${new Date(expiresAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}` : 'Ссылка скопирована — откроется у тех, у кого есть доступ' });
}

/** The sections the files belong to, in the order of the alphabet; «—» for files without a record. */
export const sectionsOf = (rows: FileRow[]) => [...new Set(rows.map((r) => r.typeName || NONE))].sort((a, b) => a.localeCompare(b, 'ru'));
